<template>
  <div class="tradie-week-block">
    <div class="week-calendar">
      <div
        v-for="day in weekDays"
        :key="day.iso"
        class="cal-day"
        :class="{
          'cal-day--today': day.isToday,
          'cal-day--selected': selectedDay === day.iso,
          'cal-day--has-events':
            row.eventsByDay[day.iso] && row.eventsByDay[day.iso].length,
        }"
        @click="$emit('day-click', { row, projectId, rowKey, day })"
      >
        <div class="cal-day-header">
          <span class="cal-day-name">{{ day.short }}</span>
          <span class="cal-day-date">{{ day.date }}</span>
        </div>
        <div class="cal-day-body">
          <template
            v-if="row.eventsByDay[day.iso] && row.eventsByDay[day.iso].length"
          >
            <div
              v-for="event in row.eventsByDay[day.iso]"
              :key="event.id"
              class="cal-event-chip"
              :class="{
                'cal-event-chip--leave': event.is_leave,
                'cal-event-chip--night':
                  !event.is_leave &&
                  isNightClockIn(event.clocked_in_at, event.timezone),
                'cal-event-chip--missing': isMissingClockOut(event, day),
              }"
            >
              <template v-if="event.is_leave">
                <span class="status-pill cal-leave-pill">{{
                  leaveTypeLabel(event.leave_type)
                }}</span>
                <span
                  v-if="event.leave_resolved_hours !== null"
                  class="cal-event-time"
                  >{{ event.leave_resolved_hours }}h</span
                >
              </template>
              <template v-else>
                <span class="status-pill" :class="eventStatusClass(event)">{{
                  eventStatusLabel(event)
                }}</span>
                <span
                  v-if="isNightClockIn(event.clocked_in_at, event.timezone)"
                  class="cal-event-night-badge"
                  >Night</span
                >
                <span
                  v-if="isMissingClockOut(event, day)"
                  class="cal-event-missing-badge"
                  title="Never clocked out — this shift may need a manual fix"
                  >Missing clock-out</span
                >
                <span class="cal-event-time">
                  <span class="cal-time-part">{{
                    formatTime(event.clocked_in_at, event.timezone)
                  }}</span>
                  <span v-if="event.clocked_out_at" class="cal-time-part"
                    >–
                    {{ formatTime(event.clocked_out_at, event.timezone) }}</span
                  >
                </span>
                <span
                  v-if="shiftEndLabel(event)"
                  class="cal-event-nextday"
                  data-test="shift-ends"
                  title="This shift ends on a later day than it started"
                  >ends {{ shiftEndLabel(event) }}</span
                >
                <span
                  v-if="isLongShift(event)"
                  class="cal-event-long-badge"
                  data-test="long-shift"
                  title="Unusually long shift. It may be a forgotten clock-out or a mistyped time."
                  >{{ calcHours(event) }}h · check</span
                >
                <span
                  v-if="hasOtherProjectConflict(event)"
                  class="day-conflict"
                  :title="event.other_project_name"
                >
                  Also clocked in at {{ event.other_project_name }}
                </span>
              </template>
            </div>
          </template>
          <span
            v-else
            class="cal-day-empty"
            :class="{ 'cal-day-empty--addable': canCreateTimesheet }"
            >-</span
          >
        </div>
      </div>
    </div>

    <div
      v-if="selectedDay"
      class="day-detail-panel"
      :class="{ 'day-detail-panel--editing': editingEventId !== null }"
    >
      <template
        v-if="
          row.eventsByDay[selectedDay] && row.eventsByDay[selectedDay].length
        "
      >
        <div
          v-for="event in row.eventsByDay[selectedDay]"
          :key="event.id"
          class="event-row"
          :class="{
            'event-row--leave': event.is_leave,
            'event-row--night':
              !event.is_leave &&
              isNightClockIn(event.clocked_in_at, event.timezone),
          }"
        >
          <div
            v-if="event.is_leave"
            class="event-row-left event-row-left--leave"
          >
            <span class="status-pill cal-leave-pill">{{
              leaveTypeLabel(event.leave_type)
            }}</span>
            <span class="event-time"
              >{{ formatLeaveDate(event.leave_start_date) }} –
              {{ formatLeaveDate(event.leave_end_date) }}</span
            >
            <span v-if="event.leave_resolved_hours !== null" class="event-hours"
              >{{ event.leave_resolved_hours }}h</span
            >
            <div v-if="event.leave_note" class="event-comment">
              "{{ event.leave_note }}"
            </div>
          </div>
          <div v-else-if="editingEventId === event.id" class="event-edit-form">
            <div class="event-edit-fields">
              <label class="event-edit-field">
                <span>Clocked in - date</span>
                <b-form-datepicker
                  v-model="editForm.in_date"
                  :max="maxFutureDate"
                  size="sm"
                  locale="en-AU"
                  today-button
                  reset-button
                  close-button
                  show-decade-nav
                />
              </label>
              <label class="event-edit-field">
                <span>Clocked in - time</span>
                <datetime-input
                  v-model="editForm.in_time"
                  type="time"
                  size="sm"
                />
              </label>
              <label class="event-edit-field">
                <span>Clocked out - date</span>
                <b-form-datepicker
                  v-model="editForm.out_date"
                  :max="maxFutureDate"
                  size="sm"
                  locale="en-AU"
                  today-button
                  reset-button
                  close-button
                  show-decade-nav
                />
              </label>
              <label class="event-edit-field">
                <span>Clocked out - time</span>
                <datetime-input
                  v-model="editForm.out_time"
                  type="time"
                  size="sm"
                />
              </label>
              <label class="event-edit-field">
                <span>Break (minutes)</span>
                <b-form-input
                  v-model.number="editForm.break_minutes"
                  type="number"
                  min="0"
                  size="sm"
                />
              </label>
              <label class="event-edit-field event-edit-field--comment">
                <span>Comment</span>
                <b-form-input v-model="editForm.comment" size="sm" />
              </label>
            </div>
            <div class="event-edit-actions">
              <b-button size="sm" variant="success" @click="$emit('save-edit')">
                Save
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="$emit('cancel-edit')"
              >
                Cancel
              </b-button>
            </div>
          </div>
          <div v-else class="event-row-left">
            <span class="status-pill" :class="eventStatusClass(event)">{{
              eventStatusLabel(event)
            }}</span>
            <span
              v-if="isNightClockIn(event.clocked_in_at, event.timezone)"
              class="cal-event-night-badge"
              >Night</span
            >
            <span
              class="zone-check-badge"
              :class="'zone-check-badge--' + zoneCheckState(event)"
              >{{ zoneCheckLabel(event) }}</span
            >
            <span class="event-time">
              {{ formatTime(event.clocked_in_at, event.timezone)
              }}<template v-if="event.clocked_out_at">
                –
                {{ formatTime(event.clocked_out_at, event.timezone) }}</template
              >
              <span
                v-if="timezoneLabel(event.timezone)"
                class="text-muted small ml-1"
                >{{ timezoneLabel(event.timezone) }}</span
              >
            </span>
            <span
              v-if="shiftEndLabel(event)"
              class="cal-event-nextday"
              data-test="shift-ends"
              title="This shift ends on a later day than it started"
              >ends {{ shiftEndLabel(event) }}</span
            >
            <span
              v-if="isLongShift(event)"
              class="cal-event-long-badge"
              data-test="long-shift"
              title="Unusually long shift. It may be a forgotten clock-out or a mistyped time."
              >Long shift · check</span
            >
            <span v-if="event.break_minutes" class="event-break"
              >{{ event.break_minutes }}min break</span
            >
            <span
              v-if="event.clocked_in_at && event.clocked_out_at"
              class="event-hours"
              >{{ calcHours(event) }}h</span
            >
            <span
              v-if="hasOtherProjectConflict(event)"
              class="day-conflict"
              :title="event.other_project_name"
            >
              Also clocked in at {{ event.other_project_name }}
            </span>
            <div v-if="event.comment" class="event-comment">
              "{{ event.comment }}"
            </div>
            <div v-if="event.admin_comment" class="event-admin-comment">
              Rejected: {{ event.admin_comment }}
            </div>
            <div
              v-if="event.photo_urls && event.photo_urls.length"
              class="event-photos"
            >
              <div
                v-for="(url, i) in event.photo_urls"
                :key="i"
                style="display: inline-block; cursor: pointer"
                @click.stop="$emit('lightbox', event.photo_urls, i)"
              >
                <img
                  :src="url"
                  class="event-photo-thumb"
                  :alt="'Photo ' + (i + 1)"
                />
              </div>
            </div>
          </div>
          <div
            v-if="!event.is_leave && editingEventId !== event.id"
            class="event-row-actions"
          >
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="$emit('view-event', event)"
              >View</b-button
            >
            <b-button-group
              v-if="event.status === 'clocked_out' && canEditTimesheet"
              size="sm"
            >
              <b-button
                variant="success"
                @click="$emit('approve', event.id, 'approved')"
                >Approve</b-button
              >
              <b-button variant="danger" @click="$emit('reject', event)"
                >Reject</b-button
              >
            </b-button-group>
            <b-button
              v-if="event.status === 'clocked_in' && canEditTimesheet"
              size="sm"
              variant="warning"
              @click="$emit('clock-out', event)"
              >Clock Out</b-button
            >
            <b-button
              v-if="canEditTimesheet"
              size="sm"
              variant="link"
              class="edit-btn"
              @click="$emit('edit', event)"
              ><b-icon-pencil
            /></b-button>
            <b-button
              v-if="!isSupervisorTradie && canDeleteTimesheet"
              size="sm"
              variant="link"
              class="delete-btn"
              @click="$emit('delete', event)"
              ><b-icon-trash
            /></b-button>
          </div>
          <div v-else-if="event.is_leave" class="event-row-actions">
            <b-button
              v-if="canEditTimesheet"
              size="sm"
              variant="link"
              class="edit-btn"
              @click="$emit('edit-leave', event)"
              ><b-icon-pencil
            /></b-button>
            <b-button
              v-if="!isSupervisorTradie && canDeleteTimesheet"
              size="sm"
              variant="link"
              class="delete-btn"
              @click="$emit('delete-leave', event)"
              ><b-icon-trash
            /></b-button>
          </div>
        </div>
      </template>
      <div v-else class="no-entries">No entries for this day.</div>
    </div>

    <div v-if="row.totalEntries === 0" class="no-entries">
      No entries this week.
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  eventStatusLabel,
  eventStatusClass,
  leaveTypeLabel,
  zoneCheckLabel,
  zoneCheckState,
  formatBusinessTime,
  timezoneLabel,
  isNightClockIn,
  shiftEndLabel,
  isLongShift,
} from '@/utils/timesheetHelpers'
import DatetimeInput from '@/components/timesheets/DatetimeInput.vue'

export default {
  name: 'TimesheetTradieWeekBlock',

  components: { DatetimeInput },

  props: {
    row: { type: Object, required: true },
    rowKey: { type: String, required: true },
    projectId: { type: [Number, String], default: null },
    weekDays: { type: Array, default: () => [] },
    selectedDay: { type: String, default: null },
    canCreateTimesheet: { type: Boolean, default: false },
    canEditTimesheet: { type: Boolean, default: false },
    canDeleteTimesheet: { type: Boolean, default: false },
    isSupervisorTradie: { type: Boolean, default: false },
    // Inline replacement for the old edit-event-modal: when editingEventId
    // matches an event in this block, its row renders editForm's fields in
    // place instead of the normal display. Both are owned by the parent tab
    // (TimesheetListTab) and mutated here in place, same as the pattern
    // SupervisorWorkerTable/Cards already use for their editRows.
    editingEventId: { type: [Number, String], default: null },
    editForm: { type: Object, default: null },
    maxFutureDate: { type: String, default: '' },
  },

  methods: {
    eventStatusLabel,
    eventStatusClass,
    timezoneLabel,
    leaveTypeLabel,
    zoneCheckLabel,
    zoneCheckState,
    isNightClockIn,
    shiftEndLabel,
    isLongShift,
    formatTime(iso, tz) {
      return formatBusinessTime(iso, undefined, tz)
    },
    formatLeaveDate(date) {
      return date ? dayjs(date).format('DD/MM/YYYY') : ''
    },
    // Informational only — a worker can legitimately be booked on more than
    // one project the same day, so this never blocks editing/adding hours.
    hasOtherProjectConflict(event) {
      return !!event && !event.is_leave && !!event.other_project_conflict
    },
    // A shift still shows "clocked_in" if it's genuinely ongoing today, so
    // only flag it once its day has passed — that's when an open shift with
    // no clock-out is actually a forgotten one, not a worker still on site.
    isMissingClockOut(event, day) {
      return (
        !!event &&
        !event.is_leave &&
        event.status === 'clocked_in' &&
        !event.clocked_out_at &&
        !!day &&
        !day.isToday
      )
    },
    calcHours(event) {
      const mins =
        (new Date(event.clocked_out_at) - new Date(event.clocked_in_at)) /
          60000 -
        (event.break_minutes || 0)
      return (mins / 60).toFixed(1)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.week-calendar {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 0;
  background: #fff;
}

.cal-day {
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background 0.12s;
  min-width: 0;
  overflow: hidden;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }

  &.cal-day--selected {
    background: #f0fdf4;

    .cal-day-header {
      background: #16a34a;

      .cal-day-name,
      .cal-day-date {
        color: #fff;
      }
    }
  }

  &.cal-day--today:not(.cal-day--selected) .cal-day-header {
    background: #eff6ff;

    .cal-day-name,
    .cal-day-date {
      color: #1d4ed8;
    }
  }
}

.cal-day-header {
  flex: 0 0 64px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px 8px;
  text-align: center;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
}

.cal-day-name {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.cal-day-date {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #111827;
}

.cal-day-body {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px 12px;
}

.cal-event-chip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 8px;
  margin-bottom: 2px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }

  .status-pill {
    display: inline-block;
    flex-shrink: 0;
    max-width: none;
    overflow: visible;
    text-overflow: clip;
  }
}

.cal-event-chip--leave {
  border-radius: 4px;
  box-shadow: inset 3px 0 0 $leave-border;
  background: $leave-bg;
  padding: 2px 4px 2px 10px;
}

.cal-event-chip--night {
  border-radius: 4px;
  box-shadow: inset 3px 0 0 #6366f1;
  background: #eef2ff;
  padding: 2px 4px 2px 10px;
}

.cal-event-nextday {
  display: inline-block;
  flex-shrink: 0;
  padding: 0.05rem 0.45rem;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 0.66rem;
  font-weight: 600;
  white-space: nowrap;
}

// Amber, the same family as the Pending status pill: it asks for a look, it
// does not say the shift is wrong.
.cal-event-long-badge {
  display: inline-block;
  flex-shrink: 0;
  padding: 0.05rem 0.45rem;
  border: 1px solid #fcd34d;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.66rem;
  font-weight: 700;
  white-space: nowrap;
}

.cal-event-night-badge {
  display: inline-block;
  flex-shrink: 0;
  padding: 0.05rem 0.4rem;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.62rem;
  font-weight: 600;
}

// Reuses the same red tokens as .status-rejected in _timesheet-shared.scss —
// a forgotten clock-out is a data-quality problem in the same family as a
// rejected entry, so it gets the same color language.
.cal-event-chip--missing {
  border-radius: 4px;
  box-shadow: inset 3px 0 0 #dc2626;
  background: #fee2e2;
  padding: 2px 4px 2px 10px;
}

.cal-event-missing-badge {
  display: inline-block;
  flex-shrink: 0;
  padding: 0.05rem 0.4rem;
  border: 1px solid #fecaca;
  border-radius: 999px;
  background: #fee2e2;
  color: #991b1b;
  font-size: 0.62rem;
  font-weight: 600;
}

.cal-leave-pill {
  background: #fff;
  color: $leave-text;
}

.cal-event-time {
  margin-top: 0;
  font-size: 11px;
  color: #6b7280;
  flex: 1 1 100%;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

.cal-time-part {
  white-space: nowrap;
}

.cal-time-part + .cal-time-part {
  margin-left: 3px;
}

.cal-day-empty {
  display: block;
  text-align: left;
  color: #d1d5db;
  font-size: 13px;
  margin-top: 0;
}

.cal-day-empty--addable {
  .cal-day:hover & {
    color: transparent;

    &::before {
      content: '+ Add';
      color: #16a34a;
      font-size: 11px;
      font-weight: 600;
    }
  }
}

.day-detail-panel {
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;

  // The edit form's date picker popup must not be clipped by the panel
  &.day-detail-panel--editing {
    overflow: visible;
  }

  .event-row {
    border-bottom: 1px solid #f3f4f6;
    border-radius: 0;

    &:last-child {
      border-bottom: none;
    }
  }
}

.event-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  flex-wrap: wrap;
}

.event-row--leave {
  background: $leave-bg;
  border-color: $leave-border;
}

.event-row--night {
  background: #eef2ff;
  border-color: #c7d2fe;
}

.event-row-left--leave {
  color: $leave-text;

  .status-pill {
    flex-shrink: 0;
  }
}

.event-row-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.event-edit-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.event-edit-fields {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}

.event-edit-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 0;
  min-width: 0;

  > span {
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
}

.event-edit-field--comment {
  grid-column: 1 / -1;
}

.event-edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.zone-check-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}
.zone-check-badge--in {
  background: #dcfce7;
  color: #166534;
}
.zone-check-badge--out {
  background: #fef3c7;
  color: #92400e;
}
.zone-check-badge--none {
  background: #f1f5f9;
  color: #64748b;
}

.event-time {
  font-size: 12px;
  color: #6b7280;
}

.event-comment {
  font-size: 11px;
  color: #6b7280;
  font-style: italic;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-admin-comment {
  font-size: 11px;
  color: #dc2626;
  font-style: italic;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-photos {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.event-photo-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
  }
}

.event-break {
  font-size: 11px;
  color: #9ca3af;
}

.event-hours {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.event-row-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.edit-btn {
  padding: 0 6px;
  color: #9ca3af;

  &:hover {
    color: #374151;
  }
}

.delete-btn {
  padding: 0 6px;
  color: #d1d5db;

  &:hover {
    color: #ef4444;
  }
}

.no-entries {
  font-size: 13px;
  color: #9ca3af;
  padding: 8px 0;
}

.day-conflict {
  display: block;
  margin-top: 2px;
  color: #b45309;
  font-size: 11px;
  flex: 1 1 100%;
}
</style>
